import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { nanoid } from 'nanoid';
import { capabilitiesApi } from '@/lib/api';
import { db } from '@/lib/db';
import { syncEngine } from '@/lib/sync';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import type {
  Capability,
  CreateCapabilityInput,
  UpdateCapabilityInput,
  PaginatedResponse,
} from '@/types';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const capabilityKeys = {
  all: ['capabilities'] as const,
  lists: () => [...capabilityKeys.all, 'list'] as const,
  list: (workspaceId: string, filters?: Record<string, unknown>) =>
    [...capabilityKeys.lists(), workspaceId, filters] as const,
  details: () => [...capabilityKeys.all, 'detail'] as const,
  detail: (workspaceId: string, id: string) =>
    [...capabilityKeys.details(), workspaceId, id] as const,
  search: (workspaceId: string, query: string) =>
    [...capabilityKeys.all, 'search', workspaceId, query] as const,
};

// ─── List capabilities ────────────────────────────────────────────────────────

interface UseCapabilitiesOptions {
  type?: string;
  status?: string;
  tags?: string[];
  page?: number;
  pageSize?: number;
}

export function useCapabilities(
  workspaceId: string | null,
  options: UseCapabilitiesOptions = {}
): UseQueryResult<PaginatedResponse<Capability>, Error> {
  const upsertCapability = useWorkspaceStore((s) => s.upsertCapability);

  return useQuery({
    queryKey: capabilityKeys.list(workspaceId ?? '', options as Record<string, unknown>),
    queryFn: async () => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }

      const params: Record<string, string | number | boolean | undefined | null> = {
        page: options.page ?? 1,
        pageSize: options.pageSize ?? 50,
      };
      if (options.type) {
        params['type'] = options.type;
      }
      if (options.status) {
        params['status'] = options.status;
      }
      if (options.tags?.length) {
        params['tags'] = options.tags.join(',');
      }

      const result = await capabilitiesApi.list(workspaceId, params);

      // Sync into local store and IndexedDB
      result.items.forEach((cap) => upsertCapability(cap));
      await db.capabilities.bulkPut(result.items);

      return result;
    },
    enabled: Boolean(workspaceId),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

// ─── Single capability ────────────────────────────────────────────────────────

export function useCapability(
  workspaceId: string | null,
  capabilityId: string | null
): UseQueryResult<Capability, Error> {
  const upsertCapability = useWorkspaceStore((s) => s.upsertCapability);

  return useQuery({
    queryKey: capabilityKeys.detail(workspaceId ?? '', capabilityId ?? ''),
    queryFn: async () => {
      if (!workspaceId || !capabilityId) {
        throw new Error('Missing workspaceId or capabilityId');
      }

      // Try local first
      const local = await db.capabilities.get(capabilityId);

      // Fetch remote in parallel
      const remote = await capabilitiesApi.get(workspaceId, capabilityId);

      // Merge: prefer newer
      const resolved =
        local && new Date(local.updatedAt) > new Date(remote.updatedAt) ? local : remote;

      upsertCapability(resolved);
      await db.capabilities.put(resolved);

      return resolved;
    },
    enabled: Boolean(workspaceId) && Boolean(capabilityId),
    staleTime: 20_000,
  });
}

// ─── Create capability ────────────────────────────────────────────────────────

export function useCreateCapability(workspaceId: string): UseMutationResult<
  Capability,
  Error,
  CreateCapabilityInput
> {
  const queryClient = useQueryClient();
  const upsertCapability = useWorkspaceStore((s) => s.upsertCapability);
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (input: CreateCapabilityInput) => {
      return capabilitiesApi.create(workspaceId, input);
    },
    onMutate: async (input) => {
      // Optimistic update
      const optimisticId = nanoid();
      const now = new Date().toISOString();
      const optimistic: Capability = {
        ...input,
        id: optimisticId,
        createdAt: now,
        updatedAt: now,
        createdBy: 'local',
        syncStatus: 'pending',
      };
      upsertCapability(optimistic);
      return { optimisticId };
    },
    onSuccess: async (capability) => {
      upsertCapability(capability);
      await db.capabilities.put(capability);
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      addToast({ title: 'Capability created', variant: 'success' });
    },
    onError: (error, _input, context) => {
      // Remove the optimistic record
      if (context?.optimisticId) {
        useWorkspaceStore.getState().removeCapability(context.optimisticId);
      }
      addToast({ title: 'Failed to create capability', description: error.message, variant: 'error' });
    },
  });
}

// ─── Update capability ────────────────────────────────────────────────────────

export function useUpdateCapability(workspaceId: string): UseMutationResult<
  Capability,
  Error,
  { id: string; data: UpdateCapabilityInput }
> {
  const queryClient = useQueryClient();
  const upsertCapability = useWorkspaceStore((s) => s.upsertCapability);
  const capabilities = useWorkspaceStore((s) => s.capabilities);
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateCapabilityInput }) => {
      return capabilitiesApi.update(workspaceId, id, data);
    },
    onMutate: async ({ id, data }) => {
      const previous = capabilities[id];
      if (previous) {
        upsertCapability({ ...previous, ...data, updatedAt: new Date().toISOString() });
      }
      return { previous };
    },
    onSuccess: async (capability) => {
      upsertCapability(capability);
      await db.capabilities.put(capability);

      // Enqueue for sync
      await syncEngine.enqueue(
        workspaceId,
        'capability',
        capability.id,
        'update',
        capability as unknown as Record<string, unknown>
      );

      await queryClient.invalidateQueries({
        queryKey: capabilityKeys.detail(workspaceId, capability.id),
      });
    },
    onError: (error, _vars, context) => {
      if (context?.previous) {
        upsertCapability(context.previous);
      }
      addToast({ title: 'Failed to save', description: error.message, variant: 'error' });
    },
  });
}

// ─── Delete capability ────────────────────────────────────────────────────────

export function useDeleteCapability(workspaceId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  const removeCapability = useWorkspaceStore((s) => s.removeCapability);
  const capabilities = useWorkspaceStore((s) => s.capabilities);
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: (id: string) => capabilitiesApi.delete(workspaceId, id),
    onMutate: async (id) => {
      const previous = capabilities[id];
      removeCapability(id);
      return { previous };
    },
    onSuccess: async (_, id) => {
      await db.capabilities.delete(id);
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.lists() });
      addToast({ title: 'Capability deleted', variant: 'success' });
    },
    onError: (error, _id, context) => {
      if (context?.previous) {
        upsertCapability(context.previous);
      }
      addToast({ title: 'Failed to delete', description: error.message, variant: 'error' });
    },
  });
}

// Helper to access upsertCapability outside of the mutation callbacks
function upsertCapability(cap: Capability) {
  useWorkspaceStore.getState().upsertCapability(cap);
}

// ─── Search capabilities ──────────────────────────────────────────────────────

export function useSearchCapabilities(
  workspaceId: string | null,
  query: string
): UseQueryResult<Capability[], Error> {
  return useQuery({
    queryKey: capabilityKeys.search(workspaceId ?? '', query),
    queryFn: async () => {
      if (!workspaceId) {
        return [];
      }
      if (!query.trim()) {
        return [];
      }
      return capabilitiesApi.search(workspaceId, query);
    },
    enabled: Boolean(workspaceId) && query.trim().length > 1,
    staleTime: 10_000,
  });
}
